import type {
  PutRoutingSkuTargetRequest,
  RoutingSkuTargetRecord,
  TargetProbeLayer,
} from '@nadoutong/contracts';
import {
  listRoutingConfigSkuTargets,
  listRoutingConfigs,
  listRoutingDefaultSkuTargets,
} from '../../api/tenantRouting.ts';

/**
 * `disabledNote` is only sent on a disable (0082 manual-disable note);
 * `undefined` leaves a stored note alone.
 */
export function routingTargetWriteBody(
  target: RoutingSkuTargetRecord,
  enabled: boolean,
  disabledNote?: string | null,
): PutRoutingSkuTargetRequest | null {
  if (target.capability_id === null) return null;
  return {
    sku_name: target.sku_name,
    vendor: target.vendor,
    model: target.model,
    capability_id: target.capability_id,
    upstream_id: target.upstream_id,
    account_id: target.account_id,
    key_bucket_id: target.key_bucket_id,
    upstream_channel_id: target.upstream_channel_id,
    enabled,
    ...(!enabled && disabledNote !== undefined ? { disabled_note: disabledNote } : {}),
  };
}

export type LocatedRoutingTarget =
  | { readonly layer: 'default'; readonly target: RoutingSkuTargetRecord }
  | { readonly layer: 'config'; readonly configId: string; readonly target: RoutingSkuTargetRecord };

export async function locateProbeMember(
  member: { readonly layer: TargetProbeLayer; readonly target_id: string },
  skuId: string,
  fetchImpl: typeof fetch,
): Promise<LocatedRoutingTarget | null> {
  if (member.layer === 'default') {
    const listed = await listRoutingDefaultSkuTargets(skuId, fetchImpl);
    const target = listed.targets.find((item) => item.id === member.target_id);
    return target === undefined ? null : { layer: 'default', target };
  }
  const configs = await listRoutingConfigs(fetchImpl);
  for (const config of configs.configs) {
    const listed = await listRoutingConfigSkuTargets(config.id, skuId, fetchImpl);
    const target = listed.targets.find((item) => item.id === member.target_id);
    if (target !== undefined) return { layer: 'config', configId: config.id, target };
  }
  return null;
}
